import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type Notification } from '../../../types/notification';
import { notificationStorage } from '../../../shared/utils/notificationStorage';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    loadNotifications: (state) => {
      state.notifications = notificationStorage.getNotifications();
      state.unreadCount = notificationStorage.getUnreadCount();
    },
    addNotification: (
      state,
      action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>
    ) => {
      const newNotification = notificationStorage.addNotification(action.payload);
      if (newNotification) {
        state.notifications = notificationStorage.getNotifications();
        state.unreadCount = notificationStorage.getUnreadCount();
      }
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      notificationStorage.markAsRead(action.payload, true); // skipEvent = true to avoid circular dependency
      state.notifications = notificationStorage.getNotifications();
      state.unreadCount = notificationStorage.getUnreadCount();
    },
    markAllAsRead: (state) => {
      notificationStorage.markAllAsRead(true); // skipEvent = true to avoid circular dependency
      state.notifications = notificationStorage.getNotifications();
      state.unreadCount = 0;
    },
    deleteNotification: (state, action: PayloadAction<string>) => {
      notificationStorage.deleteNotification(action.payload, true); // skipEvent = true to avoid circular dependency
      state.notifications = notificationStorage.getNotifications();
      state.unreadCount = notificationStorage.getUnreadCount();
    },
    clearAllNotifications: (state) => {
      notificationStorage.clearAll();
      state.notifications = [];
      state.unreadCount = 0;
    },
    addMissedNotifications: (state, action: PayloadAction<unknown[]>) => {
      const addedCount = notificationStorage.addMissedNotifications(action.payload);
      if (addedCount > 0) {
        state.notifications = notificationStorage.getNotifications();
        state.unreadCount = notificationStorage.getUnreadCount();
      }
    },
  },
});

export const {
  loadNotifications,
  addNotification,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  clearAllNotifications,
  addMissedNotifications,
} = notificationSlice.actions;

// Selectors
export const selectNotifications = (state: { notifications: NotificationState }) =>
  state.notifications.notifications;

export const selectUnreadCount = (state: { notifications: NotificationState }) =>
  state.notifications.unreadCount;

// Role and user based selectors
export const selectNotificationsByRole = (userRole?: 'traveller' | 'sender', userId?: string) =>
  (state: { notifications: NotificationState }) => {
    if (!userRole || !userId) {
      return [];
    }

    return state.notifications.notifications.filter((notification) => {
      const notifUserId = notification.userId;
      if (notifUserId && notifUserId !== userId) {
        return false;
      }

      if (notification.type === 'kyc_verification') {
        return true;
      }

      if (notification.user_role && notification.user_role !== userRole) {
        return false;
      }

      return true;
    });
  };

export const selectUnreadCountByRole = (userRole?: 'traveller' | 'sender', userId?: string) =>
  (state: { notifications: NotificationState }) => {
    if (!userRole || !userId) {
      return 0;
    }
    return state.notifications.notifications.filter(
      (notification) => {
        if (notification.isRead) return false;

        const notifUserId = notification.userId;
        if (notifUserId && notifUserId !== userId) {
          return false;
        }

        if (notification.type === 'kyc_verification') {
          return true;
        }

        if (notification.user_role && notification.user_role !== userRole) {
          return false;
        }

        return true;
      }
    ).length;
  };

export default notificationSlice.reducer;